import { useEffect, useRef, useState } from "react";

import { OrganisationTypeConstants, ProjectAccessConstants } from "../../constants";
import { InviteSelection, SelectData } from "../../models";
import { inviteOrganisationToProject } from "../../service/organisation";
import {
  searchOrganisations,
  SearchOrganisationsOrganisationType,
  searchProjectOrganisations,
} from "../../service/query";
import { ServiceError, Status } from "../../service/Shared";
import { getErrorMessageFromCode } from "../../service/ValidationErrorFormatter";
import { useAuth } from "../../useAuth";
import { Toast } from "../../widget";
import { useIsLoadingWrapper } from "./useIsLoadingWrapper";
import { useOrganisationSearch } from "./useOrganisationSearch";

const emptySelection: InviteSelection = {
  organisationType: null,
  organisationUuid: null,
  access: null,
  makeResponsibleOwner: false,
  existingDeveloperAction: null,
};

const EXISTING_DEVELOPER_ACTION_REQUIRED = "VALIDATION_INVALID_EXISTING_DEVELOPER_ACTION";

interface UseProjectAccessInviteProps {
  projectUuid: string | undefined;
  // Granted only where is_responsible_owner is true, so this is also the RO-only gate (REQ-018).
  canManageAccess: boolean;
  currentResponsibleOwnerName: string | undefined;
  onSent: () => void;
}

export interface UseProjectAccessInviteReturnData {
  selection: InviteSelection;
  onSelectionChange: (value: InviteSelection) => void;
  organisationTypeOptions: SelectData;
  organisationOptions: SelectData;
  onOrganisationSearch: (query: string) => void;
  accessOptions: SelectData;
  showMakeResponsibleOwner: boolean;
  showExistingDeveloperAction: boolean;
  existingDeveloperActionOptions: SelectData;
  existingDeveloperName: string | undefined;
  onSubmit: () => void;
  onSendInvitation: () => void;
  showSendInvitationModal: boolean;
  onCloseSendInvitationModal: () => void;
  showAssignResponsibleOwnerModal: boolean;
  selectedOrganisationName?: string;
  currentResponsibleOwnerName?: string;
  onCloseAssignResponsibleOwnerModal: () => void;
  isOnSubmitLoading: boolean;
  errors: ServiceError[] | undefined;
}

// The ProjectAccessInviteFields form, for both the project Access tab and Settings > Project access.
export const useProjectAccessInvite = ({
  projectUuid,
  canManageAccess,
  currentResponsibleOwnerName,
  onSent,
}: UseProjectAccessInviteProps): UseProjectAccessInviteReturnData => {
  const { currentOrganisationUuid } = useAuth();

  const [selection, setSelection] = useState<InviteSelection>(emptySelection);
  const [existingDeveloper, setExistingDeveloper] = useState<{ organisationUuid: string; displayName: string }>();
  const [showSendInvitationModal, setShowSendInvitationModal] = useState<boolean>(false);
  const [showAssignResponsibleOwnerModal, setShowAssignResponsibleOwnerModal] = useState<boolean>(false);
  const [isOnSubmitLoading, setIsOnSubmitLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ServiceError[] | undefined>();

  const {
    organisationOptions,
    search: searchOrganisationsByName,
    lookup: lookupOrganisation,
  } = useOrganisationSearch({ excludedProjectUuid: projectUuid });
  // What's typed in the Organisation field, so a project switch can repeat the search.
  const organisationQuery = useRef("");

  const accessOptions = ProjectAccessConstants.getInvitationAccessOptions(selection.organisationType);

  // No accepted-status check, unlike the details page - there is no invitation yet. A guest role can
  // never be the owner (BR-02871).
  const showMakeResponsibleOwner =
    canManageAccess &&
    !!selection.access &&
    ProjectAccessConstants.RESPONSIBLE_OWNER_ELIGIBLE_ROLES.includes(selection.access);

  // A Developer invite onto a project that already has one is a swap, not a second developer: the
  // api only lets it past BR-02726 if it says what becomes of the outgoing developer (BR-02873).
  const showExistingDeveloperAction = !!existingDeveloper && selection.access === OrganisationTypeConstants.DEVELOPER;

  // The project's full Developer, read once per project - the invite itself is what changes it. Filtered
  // server-side so a project with more links than one page still finds it. A failure leaves it unknown
  // and the api refuses the swap as before, which is the honest fallback.
  useEffect(() => {
    let cancelled = false;

    const loadExistingDeveloper = async (): Promise<void> => {
      setExistingDeveloper(undefined);
      if (!projectUuid) return;

      // "in", not "eq" - the service sends role as a [String] list.
      const response = await searchProjectOrganisations({
        projectUuid,
        filter: { results: { role: { operator: "in", value: OrganisationTypeConstants.DEVELOPER } } },
        paging: { limit: 1, afterCursor: null, beforeCursor: null },
      });

      if (cancelled) return;

      if (response.status !== Status.Success || !response.data?.results) {
        Toast.error({ message: "Couldn't check this project's current developer - please try again later." });
        return;
      }

      const developer = response.data.results.find((result) => result.role === OrganisationTypeConstants.DEVELOPER);

      setExistingDeveloper(
        developer
          ? { organisationUuid: developer.organisationUuid, displayName: developer.displayName ?? "" }
          : undefined
      );
    };

    loadExistingDeveloper();

    return () => {
      cancelled = true;
    };
  }, [projectUuid]);

  // A type or project switch re-runs the search - the project's own organisations aren't offered.
  useEffect(() => {
    searchOrganisationsByName(selection.organisationType, organisationQuery.current);
  }, [selection.organisationType, searchOrganisationsByName]);

  // Read here, not listed as a dependency: the check is for a project switch, not every selection.
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  // Settings picks the project after the organisation, so a switch can land on one it's already on.
  useEffect(() => {
    let cancelled = false;
    const { organisationType, organisationUuid } = selectionRef.current;

    const checkOrganisation = async (): Promise<void> => {
      if (!projectUuid || !organisationType || !organisationUuid) return;

      const response = await searchOrganisations({
        organisationType: organisationType as SearchOrganisationsOrganisationType,
        excludedProjectUuid: projectUuid,
        paging: { limit: 1, beforeCursor: "", afterCursor: "" },
        filter: { results: { organisationUuid: { operator: "eq", value: organisationUuid } } },
      });

      if (cancelled) return;

      if (response.status !== Status.Success || !response.data?.results) {
        Toast.error({ message: "Couldn't check the organisation against this project - please try again later." });
        return;
      }

      if (response.data.results.length) return;

      setSelection((prev) => (prev.organisationUuid === organisationUuid ? { ...prev, organisationUuid: null } : prev));
    };

    checkOrganisation();

    return () => {
      cancelled = true;
    };
  }, [projectUuid]);

  // The list is replaced on every search, so the name comes from what the search has offered.
  const selectedOrganisationName = selection.organisationUuid
    ? lookupOrganisation(selection.organisationUuid)?.name
    : undefined;

  const onSelectionChange = (value: InviteSelection): void => {
    if (value.organisationType !== selection.organisationType) organisationQuery.current = "";
    setSelection(value);
  };

  const onOrganisationSearch = (query: string): void => {
    organisationQuery.current = query;
    searchOrganisationsByName(selection.organisationType, query);
  };

  const onCloseSendInvitationModal = (): void => {
    setShowSendInvitationModal(false);
  };

  const onCloseAssignResponsibleOwnerModal = (): void => {
    setShowAssignResponsibleOwnerModal(false);
  };

  const onSendInvitation = useIsLoadingWrapper(async (): Promise<void> => {
    setShowSendInvitationModal(false);
    setShowAssignResponsibleOwnerModal(false);

    if (!projectUuid || !selection.organisationUuid || !selection.access || !currentOrganisationUuid) {
      return;
    }

    // One call. changeProjectResponsibleOwner cannot stand in for the flag here: it needs an active
    // project_organisation link, which a pending invitation does not have (BR-02864).
    const response = await inviteOrganisationToProject({
      projectUuid,
      targetOrganisationUuid: selection.organisationUuid,
      sourceOrganisationUuid: currentOrganisationUuid,
      role: selection.access,
      type: "Invitation",
      // A tick left from a project the user can't hand ownership of is hidden, so it isn't sent.
      makeResponsibleOwner: showMakeResponsibleOwner && (selection.makeResponsibleOwner ?? false),
      existingDeveloperAction: showExistingDeveloperAction ? (selection.existingDeveloperAction ?? null) : null,
      targetOrganisationEmailOverride: null,
    });

    if (response.status === Status.Success) {
      Toast.success({ message: "Access invitation sent" });
      onSent();
      return;
    }

    Toast.error({ message: "Access invitation failed to send" });
    setErrors(response?.errors);
  }, setIsOnSubmitLoading);

  const onSubmit = (): void => {
    const errorList: ServiceError[] = [];

    if (!projectUuid) {
      errorList.push({
        code: "NO_PROJECT",
        property: "projectUuid",
        message: "A project must be selected",
      });
    }

    if (!selection.organisationType) {
      errorList.push({
        code: "NO_ORGANISATION_TYPE",
        property: "organisationType",
        message: "An organisation type must be selected",
      });
    }

    if (!selection.organisationUuid) {
      errorList.push({
        code: "NO_ORGANISATION",
        property: "organisationUuid",
        message: "An organisation must be selected",
      });
    }

    if (!selection.access) {
      errorList.push({
        code: "NO_ACCESS_LEVEL",
        property: "access",
        message: "An access level must be selected",
      });
    }

    if (showExistingDeveloperAction && !selection.existingDeveloperAction) {
      errorList.push({
        code: EXISTING_DEVELOPER_ACTION_REQUIRED,
        property: "existingDeveloperAction",
        message: getErrorMessageFromCode(EXISTING_DEVELOPER_ACTION_REQUIRED),
      });
    }

    if (errorList.length) {
      setErrors(errorList);
      return;
    }

    setErrors(undefined);

    // Assigning the owner takes the role off whoever holds it, so it gets its own confirm (REQ-026).
    if (showMakeResponsibleOwner && selection.makeResponsibleOwner) {
      setShowAssignResponsibleOwnerModal(true);
      return;
    }

    setShowSendInvitationModal(true);
  };

  return {
    selection,
    onSelectionChange,
    organisationTypeOptions: ProjectAccessConstants.INVITATION_ORGANISATION_TYPE_OPTIONS,
    organisationOptions,
    onOrganisationSearch,
    accessOptions,
    showMakeResponsibleOwner,
    showExistingDeveloperAction,
    existingDeveloperActionOptions: ProjectAccessConstants.EXISTING_DEVELOPER_ACTION_OPTIONS,
    existingDeveloperName: existingDeveloper?.displayName,
    onSubmit,
    onSendInvitation,
    showSendInvitationModal,
    onCloseSendInvitationModal,
    showAssignResponsibleOwnerModal,
    selectedOrganisationName,
    currentResponsibleOwnerName,
    onCloseAssignResponsibleOwnerModal,
    isOnSubmitLoading,
    errors,
  };
};
